import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Lock } from "lucide-react";
import { api, formatApiError, trackEvent } from "@/lib/apiClient";
import Layout, { useSEO } from "@/components/Layout";
import { Section, Overline, BlueRule, Button } from "@/components/primitives";
import { LeadForm } from "@/components/forms";

const CATS = ["All", "Guides", "Workbooks", "Checklists", "Assessments", "Templates", "Downloads", "Tools"];

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);

  useSEO({ title: "Resources | Impactful Mindset", description: "Free and premium guides, workbooks, checklists and tools for better thinking and intentional living." });
  useEffect(() => {
    const params = cat !== "All" ? { category: cat } : {};
    api.get("/resources", { params }).then((r) => setResources(r.data));
  }, [cat]);

  return (
    <Layout>
      <Section className="pt-20 pb-10">
        <Overline>Resources</Overline>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-bold mt-4 text-[#111827]">Practical tools you can use</h1>
        <BlueRule className="mt-6" />
        <p className="text-lg text-slate-600 mt-6 max-w-2xl leading-relaxed">Guides, workbooks, checklists and tools designed to turn ideas into practice.</p>
      </Section>

      <Section className="pb-8">
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`resource-cat-${c.toLowerCase()}`}
              className={`px-4 py-2 text-sm border transition-colors ${cat === c ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "border-slate-300 text-slate-700 hover:border-[#0F2C59]"}`}>{c}</button>
          ))}
        </div>
      </Section>

      <Section className="pb-24">
        {resources.length === 0 ? (
          <div className="py-20 text-center border border-dashed border-slate-300"><p className="font-serif-display text-xl text-slate-500">No resources here yet.</p></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((r) => (
              <div key={r.id} data-testid={`resource-card-${r.slug}`} className="border border-slate-200 flex flex-col">
                <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                  <img src={r.image} alt={r.title} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center justify-between">
                    <Overline>{r.category}</Overline>
                    <span className="text-xs font-semibold px-2 py-0.5 border border-slate-300 text-slate-600">{r.is_free ? "Free" : "Premium"}</span>
                  </div>
                  <h3 className="font-serif-display text-lg font-semibold mt-3 text-[#111827]">{r.title}</h3>
                  <p className="text-slate-600 text-sm mt-2 leading-relaxed flex-1">{r.description}</p>
                  {r.requires_lead_capture ? (
                    <Button variant="secondary" className="mt-5 w-full" data-testid={`resource-get-${r.slug}`}
                      onClick={() => setActive(active === r.slug ? null : r.slug)}>
                      <Lock size={15} /> Get Free Access
                    </Button>
                  ) : r.download_url ? (
                    <Button as="a" href={r.download_url} target="_blank" rel="noopener" variant="secondary" className="mt-5 w-full"
                      onClick={() => trackEvent("resource_download", { resource: r.slug })}><Download size={15} /> Download</Button>
                  ) : (
                    <Button variant="secondary" className="mt-5 w-full" disabled>Coming Soon</Button>
                  )}
                  {active === r.slug && (
                    <div className="mt-4 border-t border-slate-200 pt-4">
                      <LeadForm resourceSlug={r.slug} campaign={`resource_${r.slug}`} source="resource_download" testidPrefix={`resource-lead-${r.slug}`} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </Layout>
  );
}
